import { Box, Container, Link, Typography } from '@mui/material';
import { JSX } from 'react';

const listStyles = {
  mt: 2,
  pl: 3,
  lineHeight: 1.7,
};

const TermsOfService = (): JSX.Element => {
  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="md">
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
          Terms of Service
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 6, textAlign: 'center' }}>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        <Box sx={{ '& > *': { mb: 4 } }}>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            Please read these Terms of Service carefully before using Group My Channels. By accessing or using our
            service, you agree to be bound by these Terms. If you do not agree, please do not use the service.
          </Typography>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              By accessing and using Group My Channels, you accept and agree to be bound by the terms and provisions of
              this agreement. These Terms apply to all versions of the service, including the web application, iOS app,
              and Android app.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              2. Eligibility
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              You must be at least 13 years of age to use this service. By using Group My Channels, you represent that
              you meet this requirement. If you are under the age of 18, you represent that your parent or legal
              guardian has reviewed and agreed to these Terms on your behalf.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              Users in the European Economic Area must be at least 16 years of age, or such other minimum age as
              required under applicable law in their jurisdiction, to use this service.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              3. License to Use
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              Group My Channels grants you a limited, non-exclusive, non-transferable, revocable license to use the
              Group My Channels service, including its web application and mobile applications, for personal,
              non-commercial use.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              This license allows you to access and use the service on devices you own or control. You are responsible
              for maintaining the confidentiality of your account. You may not:
            </Typography>
            <Box component="ul" sx={listStyles}>
              <li>Modify or copy the materials;</li>
              <li>Use the materials for any commercial purpose or public display;</li>
              <li>Attempt to reverse engineer any software contained in the service;</li>
              <li>Remove any copyright or proprietary notations.</li>
            </Box>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              This license will terminate automatically if you violate any of these restrictions.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              4. Prohibited Conduct
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              When using Group My Channels, you agree not to:
            </Typography>
            <Box component="ul" sx={listStyles}>
              <li>Use the service for any unlawful purpose or in violation of any applicable law or regulation;</li>
              <li>
                Attempt to gain unauthorized access to any part of the service, other user accounts, or our servers or
                networks;
              </li>
              <li>
                Scrape, crawl, or otherwise extract data from the service through automated means without our prior
                written consent;
              </li>
              <li>Use the service to transmit any spam, unsolicited messages, or harmful code;</li>
              <li>
                Impersonate any person or entity, or falsely state or otherwise misrepresent your affiliation with a
                person or entity;
              </li>
              <li>Create multiple accounts for abusive or deceptive purposes;</li>
              <li>Interfere with or disrupt the integrity or performance of the service.</li>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              5. Google/YouTube API Usage
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              Group My Channels uses the YouTube Data API v3 to access your YouTube subscription data. By using our
              service, you agree to:
            </Typography>
            <Box component="ul" sx={listStyles}>
              <li>
                Google&apos;s Terms of Service and Privacy Policy (
                <Link href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                  https://policies.google.com/terms
                </Link>
                );
              </li>
              <li>
                The YouTube Terms of Service (
                <Link href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">
                  https://www.youtube.com/t/terms
                </Link>
                );
              </li>
              <li>
                Allow us to access your YouTube subscription list for the purpose of organizing channels into groups;
              </li>
              <li>
                Our adherence to the Google API Services User Data Policy, including the Limited Use requirements (
                <Link
                  href="https://developers.google.com/terms/api-services-user-data-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://developers.google.com/terms/api-services-user-data-policy
                </Link>
                ).
              </li>
            </Box>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              We do not store or access your personal YouTube content, viewing history, or private data &mdash; only
              subscription metadata necessary to provide the service.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              6. Data, Privacy, and User Content
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              We collect and store only the minimum data necessary to provide our service, including:
            </Typography>
            <Box component="ul" sx={listStyles}>
              <li>Your YouTube channel subscriptions (channel IDs and basic metadata);</li>
              <li>Custom groups you create and channel assignments;</li>
              <li>Basic account information required for authentication.</li>
            </Box>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              You retain ownership of any custom groups, labels, or organizational data you create within the service
              (&ldquo;User Content&rdquo;). By using the service, you grant Group My Channels a limited, non-exclusive,
              royalty-free license to store and process your User Content solely for the purpose of providing and
              improving the service.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              For more details about data handling, please review our Privacy Policy.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              If you are located in the European Economic Area or California, you may have additional rights with
              respect to your personal data. Please refer to our Privacy Policy for details on how to exercise those
              rights.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              7. Account Termination
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              You may terminate your account at any time by revoking access through your Google account settings or by
              contacting us at support@groupmychannels.com.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              We reserve the right to suspend or terminate accounts, without notice, that: (a) violate these Terms;
              (b) engage in abusive, fraudulent, or illegal behavior; (c) engage in conduct described in Section 4
              (Prohibited Conduct); or (d) create risk or legal exposure for Group My Channels.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              Upon termination, your license to use the service will immediately cease and we may delete your User
              Content, though deletion may not be immediate.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              8. Disclaimer of Warranties
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              The service is provided on an &lsquo;as is&rsquo; and &lsquo;as available&rsquo; basis without warranty
              of any kind. Group My Channels expressly disclaims all warranties, whether express, implied, statutory, or
              otherwise, including without limitation implied warranties of merchantability, fitness for a particular
              purpose, title, and non-infringement.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              We do not warrant that the service will be uninterrupted, error-free, or free of viruses or other
              harmful components, or that any errors will be corrected.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              9. Limitation of Liability
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              To the fullest extent permitted by applicable law, in no event shall Group My Channels, its officers,
              directors, employees, or suppliers be liable for any indirect, incidental, special, consequential, or
              punitive damages, including without limitation loss of profits, data, goodwill, or other intangible
              losses, arising out of or related to your use of or inability to use the service.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              In no event shall our aggregate liability to you for all claims arising out of or relating to the service
              exceed the greater of (a) the total amount you paid to us in the twelve months preceding the claim, or
              (b) one hundred U.S. dollars ($100).
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              10. Indemnification
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              You agree to defend, indemnify, and hold harmless Group My Channels and its officers, directors,
              employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including
              reasonable attorneys&apos; fees) arising out of or in connection with: (a) your use of the service; (b)
              your violation of these Terms; (c) your violation of any third-party rights, including any intellectual
              property or privacy rights; or (d) any content you submit to or through the service.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              11. Subscriptions and Billing
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              Some features of Group My Channels may require a paid subscription. The following terms apply:
            </Typography>
            <Box component="ul" sx={listStyles}>
              <li>Subscriptions may be billed through Apple App Store, Google Play Store, or other payment providers.</li>
              <li>
                Subscriptions automatically renew unless canceled at least 24 hours before the end of the current
                billing period.
              </li>
              <li>Payment will be charged to your chosen payment method at confirmation of purchase.</li>
              <li>
                Free trials, if offered, will automatically convert to a paid subscription at the end of the trial
                period unless canceled before the trial ends.
              </li>
              <li>
                We reserve the right to change subscription pricing. Where required by law, we will provide advance
                notice of any price change before it takes effect.
              </li>
              <li>
                You can manage or cancel your subscription through your account with the applicable platform or payment
                provider.
              </li>
              <li>
                Refunds are handled according to the policies of the platform or payment provider through which the
                subscription was purchased.
              </li>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              12. Dispute Resolution and Arbitration
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              Please read this section carefully as it affects your legal rights.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              Informal Resolution. Before initiating any formal dispute, you agree to contact us at
              support@groupmychannels.com and attempt to resolve the dispute informally for at least 30 days.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              Binding Arbitration. If informal resolution is unsuccessful, any dispute, claim, or controversy arising
              out of or relating to these Terms or the service shall be resolved by binding arbitration administered by
              the American Arbitration Association (&ldquo;AAA&rdquo;) under its Consumer Arbitration Rules, rather than
              in court. Judgment on the award may be entered in any court of competent jurisdiction.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              Class Action Waiver. You agree that any arbitration or legal proceeding shall be conducted on an
              individual basis only, and not as a class, consolidated, or representative action. You expressly waive
              any right to bring or participate in a class action lawsuit.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              Exceptions. Either party may seek emergency injunctive relief from a court of competent jurisdiction to
              prevent irreparable harm pending arbitration. Claims within the jurisdiction of small claims court may
              also be brought there.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              13. Platform-Specific Terms
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              This agreement is between you and Group My Channels, not Apple Inc. or Google LLC. Neither Apple nor
              Google is responsible for the application or its content.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              For applications accessed via Apple devices, use of the app is also subject to the Usage Rules set forth
              in the Apple Media Services Terms and Conditions.
            </Typography>
            <Box component="ul" sx={listStyles}>
              <li>Apple has no obligation to provide maintenance or support services for the application.</li>
              <li>
                In the event of any failure of the application to conform to any applicable warranty, you may notify
                Apple, and Apple will refund the purchase price for the app (if any). To the maximum extent permitted
                by applicable law, Apple will have no other warranty obligation.
              </li>
              <li>
                Apple is not responsible for addressing any claims by you or any third party relating to the
                application, including product liability claims, claims that the application fails to conform to legal
                requirements, or claims arising under consumer protection laws.
              </li>
              <li>Apple and its subsidiaries are third-party beneficiaries of this Agreement and may enforce its terms.</li>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              14. Governing Law
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              These Terms shall be governed and construed in accordance with the laws of the State of Georgia, United
              States, without regard to its conflict of law provisions. Subject to the arbitration clause above, you
              consent to the exclusive jurisdiction and venue of the state and federal courts located in Georgia for
              any dispute not subject to arbitration.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              15. Changes to Terms
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              We reserve the right to update or modify these Terms at any time. For material changes, we will provide
              at least 14 days&apos; advance notice via email (if you have provided one) or via a prominent in-app or
              on-site notice before the changes take effect.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              Your continued use of the service after the effective date of revised Terms constitutes your acceptance
              of those changes. If you do not agree to the revised Terms, you must stop using the service.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              16. Severability
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              If any provision of these Terms is found to be invalid or unenforceable by a court of competent
              jurisdiction, that provision shall be enforced to the maximum extent permissible and the remaining
              provisions will remain in full force and effect.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              17. Entire Agreement
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and Group My
              Channels with respect to the service and supersede all prior agreements, representations, and
              understandings of any kind between you and Group My Channels.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              18. Contact Information
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              If you have any questions about these Terms of Service, please contact us at:
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              Group My Channels
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              Email: support@groupmychannels.com
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsOfService;
